import React, { useEffect, useState } from 'react';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Spinner from './spinner/Spinner';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import { fileToBase64 } from '../helpers/convertToBase64';
import useFetchDatav2 from '../hooks/useFetchDatav2';

const schema = yup
  .object({
    certTemplateImage: yup
      .mixed()
      .required('Please select an image')
      .test('fileSize', 'Please upload an image less than 10MB', (value) => {
        const maxSize = 10485760;
        return value && value.length > 0 ? value[0]?.size <= maxSize : true;
      }),
    feedbackSurveyLink: yup
      .string()
      .required('Please provide feedback survey link'),
    programName: yup.string().required('Please enter the program name'),
    programDuration: yup.string(),
    certificateTextTemplate: yup.string(),
    reviewDeadlineDays: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' ? undefined : value
      )
      .nullable(),
  })
  .required();

const inputClass =
  'border-[#CBCBCB] border-[1px] px-2 py-2 rounded-lg focus:outline-none';
const errorInputClass = 'border-red-600';

function CreateCohortCertSettings({ cohortId, certificateType, toggleModal }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });
  const queryClient = useQueryClient();

  const { data: cohortData } = useFetchDatav2(
    ['cohort', cohortId],
    `/cohorts/${cohortId}`,
    {},
    null,
    !!cohortId
  );

  const cohort = cohortData?.data?.cohort || cohortData?.data || {};
  const courseId = cohort.course_id || '';
  const cohortModuleIds = (() => {
    try {
      const raw = cohort.modules;
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string') return JSON.parse(raw);
      return [];
    } catch {
      return [];
    }
  })();

  const { data: coursesData } = useFetchDatav2(
    ['courses'],
    '/courses',
    {},
    null,
    true
  );

  const { data: courseModulesData } = useFetchDatav2(
    ['course-modules-cert', courseId],
    '/course-modules',
    { params: { courseId, is_paid_module: false } },
    null,
    !!courseId
  );

  useEffect(() => {
    if (!coursesData || !courseId) return;
    const courses = coursesData?.data?.courses || [];
    const course = courses.find((c) => c.id === courseId);
    if (course?.courseName) {
      setValue('programName', course.courseName);
    }
  }, [coursesData, courseId, setValue]);

  useEffect(() => {
    if (!courseModulesData) return;
    const allModules = courseModulesData?.data?.course_modules || [];
    const cohortModules =
      cohortModuleIds.length > 0
        ? allModules.filter((m) => cohortModuleIds.includes(m.id))
        : allModules;

    const durations = cohortModules
      .map((m) => m.hours_of_learning)
      .filter(Boolean);

    if (durations.length === 1) {
      setValue('programDuration', durations[0]);
    } else if (durations.length > 1) {
      setValue('programDuration', durations.join(' + '));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseModulesData, setValue]);

  const postMutation = useMutation(
    (data) =>
      axiosInstancev2.post(
        `/cohorts/${cohortId}/cert-settings/${certificateType}`,
        data
      ),
    {
      onSuccess: () => {
        toast.success('Cohort certificate settings successfully created!');
        toggleModal();
        setIsSubmitting(false);
        queryClient.invalidateQueries([
          'cohort-cert-settings',
          cohortId,
          certificateType,
        ]);
        queryClient.invalidateQueries([
          'cohort-cert-settings-check',
          cohortId,
          certificateType,
        ]);
      },
      onError: (error) => {
        toast.error(
          `Creation failed! ${
            error?.response?.data?.error || 'Please try again.'
          }`
        );
        setIsSubmitting(false);
      },
    }
  );

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    const base64Url = data.certTemplateImage[0]
      ? await fileToBase64(data.certTemplateImage[0])
      : '';

    const requestBody = {
      cert_template: base64Url,
      feedback_survey_link: data.feedbackSurveyLink,
      name_style_classnames: ' ',
      specialization_style_classnames: ' ',
      certification_date_style_classnames: ' ',
      valid_cert_qr_code_style_classnames: ' ',
      certificate_text_template: data.certificateTextTemplate || '',
      program_name: data.programName || '',
      program_duration: data.programDuration || '',
      review_deadline_days: data.reviewDeadlineDays || 7,
    };

    postMutation.mutate(requestBody);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div>
      <form
        className="text-gray-700 space-y-4 mb-6"
        onSubmit={handleSubmit(onSubmit)}
      >
        {imagePreviewUrl && (
          <img
            src={imagePreviewUrl}
            alt="Certificate Template Preview"
            className="w-full max-w-sm h-auto object-contain rounded-md border"
          />
        )}
        <div className="flex flex-col space-y-1">
          <label htmlFor="certTemplateImage" className="text-base font-medium">
            Certificate Template Image
          </label>
          <input
            {...register('certTemplateImage', { required: true })}
            accept="image/gif, image/jpeg, image/png"
            type="file"
            id="certTemplateImage"
            className={`${inputClass} ${
              errors.certTemplateImage ? errorInputClass : ''
            }`}
            onChange={handleImageChange}
          />
          {errors.certTemplateImage && (
            <span className="text-red-600 text-xs mt-1">
              {errors.certTemplateImage?.message}
            </span>
          )}
          <span className="text-gray-400 text-xs">
            Upload the background template. Name, description, date, and QR code
            are overlaid automatically.
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col space-y-1">
            <label htmlFor="programName" className="text-base font-medium">
              Program Name
            </label>
            <input
              {...register('programName')}
              type="text"
              id="programName"
              placeholder="Software Development"
              className={`${inputClass} ${
                errors.programName ? errorInputClass : ''
              }`}
            />
            <span className="text-gray-400 text-xs">
              Auto-filled from course name
            </span>
          </div>
          <div className="flex flex-col space-y-1">
            <label htmlFor="programDuration" className="text-base font-medium">
              Program Duration
            </label>
            <input
              {...register('programDuration')}
              type="text"
              id="programDuration"
              placeholder="20 Hours"
              className={`${inputClass} ${
                errors.programDuration ? errorInputClass : ''
              }`}
            />
            <span className="text-gray-400 text-xs">
              Auto-filled from module hours of learning
            </span>
          </div>
        </div>
        <div className="flex flex-col space-y-1">
          <label
            htmlFor="certificateTextTemplate"
            className="text-base font-medium"
          >
            Certificate Text Template (optional)
          </label>
          <textarea
            {...register('certificateTextTemplate')}
            id="certificateTextTemplate"
            rows={3}
            placeholder="Leave empty to use the default text, or customize using variables below"
            className={`${inputClass} ${
              errors.certificateTextTemplate ? errorInputClass : ''
            }`}
          />
          <span className="text-gray-400 text-xs">
            Variables: {'{duration}'}, {'{program_name}'}, {'{core_tracks}'},{' '}
            {'{specialization_tracks}'}, {'{final_track}'}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col space-y-1">
            <label
              htmlFor="feedbackSurveyLink"
              className="text-base font-medium"
            >
              Feedback Survey Link
            </label>
            <input
              {...register('feedbackSurveyLink', { required: true })}
              type="url"
              id="feedbackSurveyLink"
              placeholder="https://forms.gle/..."
              className={`${inputClass} ${
                errors.feedbackSurveyLink ? errorInputClass : ''
              }`}
            />
            {errors.feedbackSurveyLink && (
              <span className="text-red-600 text-xs mt-1">
                {errors.feedbackSurveyLink?.message}
              </span>
            )}
          </div>
          <div className="flex flex-col space-y-1">
            <label
              htmlFor="reviewDeadlineDays"
              className="text-base font-medium"
            >
              Review Deadline (Days)
            </label>
            <input
              {...register('reviewDeadlineDays')}
              type="number"
              id="reviewDeadlineDays"
              defaultValue={7}
              className={`${inputClass} ${
                errors.reviewDeadlineDays ? errorInputClass : ''
              }`}
            />
            <span className="text-gray-400 text-xs">
              Days students have to review before issuance
            </span>
          </div>
        </div>
        {isSubmitting ? (
          <div className="w-full flex justify-center items-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex justify-between mt-8">
            <button
              className="border-[1px] border-claret-500 px-4 py-1 rounded-md text-claret-500 text-sm space-x-2 flex items-center"
              type="button"
              onClick={toggleModal}
            >
              <CancelIcon fontSize="inherit" />
              <p>Cancel</p>
            </button>
            <button
              className="bg-claret-500 px-4 py-1 rounded-md text-white text-sm space-x-2 flex items-center"
              type="submit"
            >
              <SaveIcon fontSize="inherit" />
              <p>Create Cert Settings</p>
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

export default CreateCohortCertSettings;
